import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { IndianRupee, Plus, Trash2, Pencil, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = '';
const STAGES = [
  { key: 'cutting', name: 'Cutting' },
  { key: 'prep_printing', name: 'Printing' },
  { key: 'prep_skiving', name: 'Skiving' },
  { key: 'prep_marking', name: 'Marking' },
  { key: 'prep_crimping', name: 'Crimping' },
  { key: 'prep_nailing', name: 'Nailing' },
  { key: 'prep_socks', name: 'Socks' },
  { key: 'closing', name: 'Closing' },
  { key: 'lasting', name: 'Lasting' },
  { key: 'pasting', name: 'Pasting' },
  { key: 'finishing', name: 'Finishing' },
  { key: 'packing', name: 'Packing' },
  { key: 'dispatch', name: 'Dispatch' },
];

export default function WagesMaster() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [filterModel, setFilterModel] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [uniqueModels, setUniqueModels] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ model_code: '', stage: '', rate_per_pair: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [allModelCodes, setAllModelCodes] = useState([]);

  const fetchItems = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterModel) params.append('model_code', filterModel);
      if (filterStage) params.append('stage', filterStage);
      const { data } = await axios.get(`${API}/api/wages-master?${params}`, { withCredentials: true });
      setItems(data.items || []);
      const models = [...new Set((data.items || []).map(i => i.model_code))];
      setUniqueModels(models);
    } catch {}
  }, [filterModel, filterStage]);

  const fetchAllModelCodes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/wages-master/model-codes`, { withCredentials: true });
      setAllModelCodes(data.model_codes || []);
    } catch {}
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchAllModelCodes(); }, [fetchAllModelCodes]);

  const openAdd = () => { setError(''); setEditItem(null); setForm({ model_code: '', stage: '', rate_per_pair: '' }); setAddModal(true); };
  const openEdit = (item) => { setError(''); setEditItem(item); setForm({ model_code: item.model_code, stage: item.stage, rate_per_pair: String(item.rate_per_pair) }); setAddModal(true); };

  const saveRate = async () => {
    setError('');
    if (!form.model_code || !form.stage || !form.rate_per_pair) { setError('All fields are required'); return; }
    setLoading(true);
    try {
      const payload = { model_code: form.model_code, stage: form.stage, rate_per_pair: parseFloat(form.rate_per_pair) };
      if (editItem) {
        await axios.put(`${API}/api/wages-master`, payload, { withCredentials: true });
        toast.success(`Rate updated for ${form.model_code} / ${STAGES.find(s => s.key === form.stage)?.name}`);
      } else {
        await axios.post(`${API}/api/wages-master`, payload, { withCredentials: true });
        toast.success(`Rate set for ${form.model_code} / ${STAGES.find(s => s.key === form.stage)?.name}`);
      }
      setAddModal(false);
      fetchItems();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save rate');
    } finally { setLoading(false); }
  };

  const deleteRate = async (model_code, stage) => {
    if (!window.confirm(`Delete rate for ${model_code} / ${STAGES.find(s => s.key === stage)?.name}?`)) return;
    try {
      await axios.delete(`${API}/api/wages-master?model_code=${encodeURIComponent(model_code)}&stage=${stage}`, { withCredentials: true });
      toast.success('Rate deleted');
      fetchItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/wages-master/template`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', 'wages_master_template.xlsx'); document.body.appendChild(link);
      link.click(); link.remove();
    } catch { toast.error('Failed to download template'); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/wages-master/upload`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(data.message);
      if (data.warnings) toast.warning(`Warnings: ${data.warnings.join(', ')}`);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    e.target.value = '';
  };

  // Group items by model_code for matrix view
  const grouped = {};
  items.forEach(item => {
    if (!grouped[item.model_code]) grouped[item.model_code] = {};
    grouped[item.model_code][item.stage] = item.rate_per_pair;
  });

  return (
    <div className="p-6" data-testid="wages-master-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tighter text-[#09090B]">Wages Master</h1>
          <p className="text-sm text-zinc-500">Model code-wise rate per pair — applies to all sizes of the model</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-9 rounded-sm text-xs" onClick={downloadTemplate} data-testid="download-wm-template">
            <Download size={12} className="mr-1" /> Template
          </Button>
          <a
            href="/api/wages-master/export"
            download
            className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-sm text-xs border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-900 no-underline"
            data-testid="download-wm-export"
          >
            <Download size={12} className="mr-1" /> Download Rates
          </a>
          {isAdmin && (
            <>
              <label className="cursor-pointer">
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUpload} data-testid="upload-wm-file" />
                <div className="flex items-center gap-1 h-9 px-3 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 cursor-pointer">
                  <Upload size={12} /> Bulk Upload
                </div>
              </label>
              <Button className="h-9 rounded-sm bg-[#09090B]" onClick={openAdd} data-testid="add-rate-btn">
                <Plus size={14} className="mr-1.5" /> Add Rate
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Filter by Model Code</Label>
            <Select value={filterModel || 'all'} onValueChange={v => setFilterModel(v === 'all' ? '' : v)}>
              <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="wm-filter-model">
                <SelectValue placeholder="All Models" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Models</SelectItem>
                {uniqueModels.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Filter by Stage</Label>
            <Select value={filterStage || 'all'} onValueChange={v => setFilterStage(v === 'all' ? '' : v)}>
              <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="wm-filter-stage">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <span className="text-sm text-zinc-500">{items.length} rate{items.length !== 1 ? 's' : ''} configured</span>
          </div>
        </div>
      </div>

      {/* Rate Matrix */}
      <div className="bg-white border border-zinc-200 mb-6">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <IndianRupee size={18} className="text-zinc-500" />
          <h2 className="font-heading font-bold text-lg">Rate Matrix (per pair) — by Model Code</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="wages-master-table">
            <thead className="bg-zinc-50">
              <tr>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold sticky left-0 bg-zinc-50">Model Code</th>
                {STAGES.map(s => (
                  <th key={s.key} className="text-center p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{s.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(grouped).sort().map((model, idx) => (
                <tr key={model} className={`border-t border-zinc-200 ${idx % 2 === 1 ? 'bg-zinc-50/30' : ''}`} data-testid={`wm-row-${model}`}>
                  <td className="p-3 font-mono text-xs font-medium sticky left-0 bg-white">{model}</td>
                  {STAGES.map(s => (
                    <td key={s.key} className="p-1.5 text-center">
                      {grouped[model][s.key] !== undefined ? (
                        <div className="flex items-center justify-center gap-1">
                          <span className="font-mono text-xs font-bold">{grouped[model][s.key]}</span>
                          {isAdmin && (
                            <>
                              <button onClick={() => openEdit({ model_code: model, stage: s.key, rate_per_pair: grouped[model][s.key] })} className="text-zinc-400 hover:text-zinc-600" data-testid={`edit-rate-${model}-${s.key}`}><Pencil size={10} /></button>
                              <button onClick={() => deleteRate(model, s.key)} className="text-zinc-400 hover:text-red-500" data-testid={`delete-rate-${model}-${s.key}`}><Trash2 size={10} /></button>
                            </>
                          )}
                        </div>
                      ) : (
                        isAdmin ? (
                          <button onClick={() => { setForm({ model_code: model, stage: s.key, rate_per_pair: '' }); setEditItem(null); setError(''); setAddModal(true); }} className="text-zinc-300 hover:text-zinc-500 text-xs" data-testid={`add-rate-${model}-${s.key}`}>+ Set</button>
                        ) : (
                          <span className="text-zinc-300 text-xs">—</span>
                        )
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {Object.keys(grouped).length === 0 && (
                <tr><td colSpan={STAGES.length + 1} className="p-8 text-center text-zinc-500">No rates configured yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent className="max-w-sm" data-testid="wages-master-dialog">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Rate' : 'Add New Rate'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">{error}</div>}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Model Code *</Label>
              {editItem ? (
                <Input value={form.model_code} readOnly className="mt-1 h-8 rounded-sm text-sm bg-zinc-50 font-mono" />
              ) : (
                <div className="mt-1">
                  <Input value={form.model_code} onChange={e => setForm(p => ({ ...p, model_code: e.target.value }))} placeholder="e.g. EP1642BROWN" className="h-8 rounded-sm text-sm font-mono" data-testid="wm-model-input" list="model-list" />
                  <datalist id="model-list">{allModelCodes.map(s => <option key={s} value={s} />)}</datalist>
                </div>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Stage *</Label>
              {editItem ? (
                <Input value={STAGES.find(s => s.key === form.stage)?.name || form.stage} readOnly className="mt-1 h-8 rounded-sm text-sm bg-zinc-50" />
              ) : (
                <Select value={form.stage || 'none'} onValueChange={v => setForm(p => ({ ...p, stage: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="mt-1 h-8 rounded-sm text-sm" data-testid="wm-stage-select"><SelectValue placeholder="Select Stage" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select Stage</SelectItem>
                    {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Rate per Pair *</Label>
              <Input type="number" step="0.01" value={form.rate_per_pair} onChange={e => setForm(p => ({ ...p, rate_per_pair: e.target.value }))} className="mt-1 h-8 rounded-sm text-sm font-mono" data-testid="wm-rate-input" />
            </div>
            <p className="text-[10px] text-zinc-400">This rate applies to all sizes of this model code (e.g., EP1642BROWN_6, _7, _8, etc.)</p>
            <div className="flex justify-end gap-3 pt-2 border-t border-zinc-200">
              <Button variant="outline" className="h-9 rounded-sm" onClick={() => setAddModal(false)}>Cancel</Button>
              <Button className="h-9 rounded-sm bg-[#09090B]" onClick={saveRate} disabled={loading} data-testid="wm-save-btn">{loading ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
